import type { WalkthroughStep } from "../types";

/**
 * Spawn Index's tour, started by the demo button or by asking for it.
 *
 * The pack it runs over is the half-extracted download — two stream-only packs
 * and nothing that declares either of them — so every step below has a real row
 * to point at, including the guessed codes, which are the thing this tool is
 * most often wrong about in a way a reader has to be told.
 *
 * The `welcome` step announces the sample and is dropped by the island when no
 * sample is loaded, because "not your files" is false over a folder the reader
 * picked themselves.
 *
 * Also reachable without loading a sample. The island renders a permanent
 * "Show me how this works" control in its intake row (`ShowTourButton`), which
 * raises the same signal, because gating every route to the tour on the demo
 * button left the tool's actual user — somebody who dropped their own folder —
 * with no tutorial and no way to ask for one. So a step may still assume a
 * result is on screen, and may no longer assume that result came from us:
 * anything that describes the sample by name says so explicitly rather than
 * relying on there being no other way in.
 */
export const steps: WalkthroughStep[] = [
  {
    id: "welcome",
    title: "This is a sample pack",
    body: "A built-in half-extracted download is loaded, not your files. Five stops, then drop your own folder.",
    placement: "center",
  },
  {
    id: "drop",
    title: "Where your folder goes",
    body: "A resource, a whole `resources/` folder, or a zip. Only text files are opened; binary assets are counted by name and size and never read.",
    selector: "[data-walkthrough='spi-drop']",
    placement: "bottom",
  },
  {
    id: "totals",
    title: "Read the Guessed tile",
    body: "Codes from a `vehicles.meta` are read. Codes with no meta behind them are guessed off stream file names, counted separately here, and badged in the list — because a guessed spawn name that does not work is worse than no list at all.",
    selector: "[data-walkthrough='spi-totals']",
    placement: "bottom",
  },
  {
    id: "packs",
    title: "One section per resource",
    body: "Two folders, not one: the pack declares nothing, so each `stream/` folder is its own resource. Codes claimed by two packs are marked, because only one of them answers when a player types it.",
    selector: "[data-walkthrough='spi-packs']",
    placement: "top",
  },
  {
    id: "findings",
    title: "What was not checked",
    body: "The scope note and everything the read refused are listed beside the findings. This tool reads what a folder declares — it never launches the game, so a code listed here is a code the files claim.",
    selector: "[data-walkthrough='spi-findings']",
    placement: "top",
  },
  {
    id: "exports",
    title: "The list, in four shapes",
    body: "Plain text, CSV, a `vehicles.lua`, or a QB-Core shared file. Every one carries every code found, not just the rows drawn — and a sample-pack export downloads with a sample- prefix.",
    selector: "[data-walkthrough='spi-exports']",
    placement: "top",
  },
];

export const finishLabel = "Drop my own folder";
